// ABOUTME: This component displays and manages fuel surcharge records for a shipper
// ABOUTME: Provides CRUD operations through modals and displays data in a grid

import { Box, IconButton } from "@mui/material";
import { useTheme } from "@mui/material";

import { tokens } from "../../../../../theme";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ShTextSkeleton from "../../../../theme/skeleton/ShTextSkeleton";
import { useGetFuelSurchargesQuery, useDeleteFuelSurchargeMutation, useAddFuelSurchargeMutation, useUpdateFuelSurchargeMutation } from "../../../../../newapi/surcharge/surchargeSlice";
import AddIcon from '@mui/icons-material/Add';
import Shbutton from "../../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../../components/theme/buttons/ButtonGroup";
import MDynamicValues from "../../../Modals/MDynamicValues";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { showToastMessageDeleteRequest, showToastMessagePostRequest, showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../../../utils/toasts";
import ShDataGrid2 from "../../../../theme/datagrid2/ShDataGrid2";
import MConfirmDelete from "../../../Modals/MConfirmDelete";
import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../../utils/permissions';
import { useSelector } from "react-redux";
import Text from "../../../../theme/text/Text";

const gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const GridActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return (
        <ButtonGroup version={2}>
            {props.hasModifyPrivilege && (
            <Shbutton
                className={"group-btn"}
                color={theme.palette.mode === "dark" ? "blue" : "primary"}
                onClick={() => props.handleOpen(true)}
                endIcon={<AddIcon/>}
                variant="contained"
            >
                {t('new_fuel_surcharge')}
            </Shbutton>
            )}
        </ButtonGroup>
    )
}

const FuelSurcharge = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [data, setData] = useState([]);
    const [surchargeInfo, setSurchargeInfo] = useState(null);
    const [config,setConfig] = useState({
        shipperId: props.shipperId
    })

    const hasModifyPrivilege = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.SHIPPER_MANAGE])
    );

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);

    const handleCloseAdd = () => {
        setAddModalOpen(false);
    };

    const handleCloseUpdate = () => {
        setUpdateModalOpen(false);
        setSurchargeInfo(null);
    };

    const [surchargeId, setSurchargeId] = useState(null);
    const handleDeleteSurchargeClick = (id) => {
        setSurchargeId(id);
    }

    useEffect(() => {
        if (surchargeId) {
            setConfirmDeleteModalOpen(true);
        }
    }, [surchargeId]);

    const [DeleteShipperSurcharge,{isLoading: deleteLoading}] = useDeleteFuelSurchargeMutation();
    const handleDeleteSurcharge = async (id) => {
        try {
           const response = await DeleteShipperSurcharge(surchargeId).unwrap();
           showToastMessageDeleteRequest(t, response);
        } catch (err) {
            showToastMessageDeleteRequest(t, {success: false, error: err.error});
        }
    };

    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const confirmDeleteModal = {
        open: confirmDeleteModalOpen,
        setOpen: setConfirmDeleteModalOpen,
        confirm: handleDeleteSurcharge
    }

    useEffect(() => {
        if (surchargeInfo) {
            setUpdateModalOpen(true);
        }
    }, [surchargeInfo]);

    const [addShipperSurcharge, {isLoading: addLoading}] = useAddFuelSurchargeMutation();
    const handleAddSurcharge = async (values) => {
        try {
            if (addLoading) return;

            const surchargeData = {
                shipperId: props.shipperId,
                date: values.date,
                percentage: values.percentage,
                type: values.type
            };

           const response = await addShipperSurcharge(surchargeData).unwrap();
           showToastMessagePostRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t('create_failed'), t, err);
        }
    };

    const [updateShipperSurcharge, {isLoading: updateLoading}] = useUpdateFuelSurchargeMutation();
    const handleUpdateSurcharge = async (values) => {
        try {
            if (updateLoading) return;

            const surchargeData = {
                id: surchargeInfo.id,
                shipperId: props.shipperId,
                date: values.date,
                percentage: values.percentage,
                type: values.type
            };

           const response = await updateShipperSurcharge(surchargeData).unwrap();
           showToastMessageUpdateRequest(t, response);
        } catch (err) {
            showToastMessageRequestError(t('update_failed'), t, err);
        }
    };

    const addFields = [
        {
            name: "date",
            label: "date",
            type: "date",
            required: true
        },
        {
            name: "percentage",
            label: "percentage",
            type: "integer",
            defaultValue: 0,
            required: true
        },
        {
            name: "type",
            label: "type",
            type: "list",
            options: [{name: t("pallet"), id: "pallet"}, {name: t("parcel"), id: "parcel"}],
            required: true
        }
    ];

    const updateFields = surchargeInfo ? [
        {
            name: "date",
            label: "date",
            type: "date",
            defaultValue: surchargeInfo.date,
            required: true
        },
        {
            name: "percentage",
            label: "percentage",
            type: "integer",
            defaultValue: surchargeInfo.percentage,
            required: true
        },
        {
            name: "type",
            label: "type",
            type: "list",
            options: [{name: t("pallet"), id: "pallet"}, {name: t("parcel"), id: "parcel"}],
            defaultValue: surchargeInfo.type,
            required: true
        }
    ] : [];

    useEffect(() => {
        props.replaceView({
            ...props.config,
            mobileActions: <GridActions handleOpen={() => setAddModalOpen(true)} hasModifyPrivilege={hasModifyPrivilege}/>,
            gridOptions:gridOptions,
        });

        return () => {
            // cleanup
        }
    },[]);

    const columns = [
        {
            field: "date",
            headerName: t('date'),
            flex:1.5,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                let date = new Date(params.row.date);
                return (
                <Text>
                    {date.toLocaleDateString(i18n.language)}
                </Text>
                );
            }
        },
        {
            field: "percentage",
            headerName: t('percentage'),
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return `${params.row.percentage}%`
            }
        },
        {
            field: "type",
            headerName: t('type'),
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return t(params.row.type)
            }
        },
        {
            field: "actions",
            headerName: "",
            flex:1,
            renderCell : (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return <Box>
                    {hasModifyPrivilege && (
                        <>
                    <IconButton type="button" onClick={() => setSurchargeInfo(params.row)}
                    sx={{
                        color: colors.txt["primary"]
                    }}>
                        <EditIcon/>
                    </IconButton>
                    <IconButton type="button" onClick={() => handleDeleteSurchargeClick(params.row.id)}
                    sx={{
                        color: colors.red[400]
                    }}>
                        <DeleteIcon/>
                    </IconButton>
                </>
                    )}
            </Box>
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            <ShDataGrid2 key={theme.palette.mode}
                content={useGetFuelSurchargesQuery}
                itemId={props.shipperId}
                config={config}
                gridActions={<GridActions handleOpen={() => setAddModalOpen(true)} hasModifyPrivilege={hasModifyPrivilege}></GridActions>}
                title={t('fuel_surcharges')}
                gridOptions={gridOptions}
                isSidepanelGrid
                columns={columns}
                rows={data}>
            </ShDataGrid2>

            <MDynamicValues
                open={addModalOpen}
                handleClose={handleCloseAdd}
                title={t("add_fuel_surcharge")}
                fields={addFields}
                onConfirm={handleAddSurcharge}
                labelWidth={140}
            />

            <MDynamicValues
                open={updateModalOpen}
                handleClose={handleCloseUpdate}
                title={t("update_fuel_surcharge")}
                fields={updateFields}
                onConfirm={handleUpdateSurcharge}
                labelWidth={140}
            />

            <MConfirmDelete
                open={confirmDeleteModal.open}
                handleClose={() => confirmDeleteModal.setOpen(false)}
                data={confirmDeleteModal.data}
                confirm={confirmDeleteModal.confirm}
            />
        </Box>
    );
}

export default FuelSurcharge;
